/* eslint-disable react/prop-types */
import { Tooltip } from "react-tooltip";

/**
 * CommercialFeesContent — Figma-matched layout for the fees section.
 * Renders Transaction Fee, Earnest Money Deposit, Buyer's Agent Commission,
 * and Important Notice using the same business logic as KeyInfoDetails
 * but with the commercial PDP visual design (icon boxes, structured rows).
 */
const CommercialFeesContent = ({ pdpData, pdpPropertyFees, emd }) => {
  const propertyDetails = pdpData?.propertyDetails || {};
  const { detailedAttributes, commonAttributes } = propertyDetails;
  const { propCategory } = commonAttributes || {};
  const { isPrelistAsset } = detailedAttributes || {};
  const propertySpecialDetails = pdpData?.propertySpecialDetails || {};
  const {
    pdpEnabledFeatures,
    shortSaleFlag,
    buyerAgentCommissionPercent,
    buyerAgentCommissionAmount,
    buyerAgentCommissionString,
    hideBuyerAgentCommissionFlag,
    brokerCoOpSellerCategoryFlag,
  } = propertySpecialDetails;
  const { listingType } = pdpData?.listingDetail || {};

  const getFormatPrice = (amnt) => {
    if (amnt?.length === 0 || isNaN(amnt)) return "";
    return amnt?.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
  };

  const getBuyersPremimum = () => {
    if (pdpEnabledFeatures?.includes("SELLER")) {
      return pdpPropertyFees?.sellerPremiumText;
    } else if (
      pdpData?.revisedBuyerPremium !== undefined &&
      pdpData?.revisedBuyerPremiumPercentage
    ) {
      if (pdpData?.revisedBuyerPremium && pdpData?.revisedBuyerPremium <= 0) {
        return pdpPropertyFees?.buyerPremiumTxtAmt0;
      } else if (
        pdpData?.revisedBuyerPremiumPercentage &&
        pdpData?.revisedBuyerPremiumPercentage <= 0
      ) {
        return pdpPropertyFees?.buyerPremiumTxtPer0;
      } else {
        return pdpPropertyFees?.buyerPremiumDetails
          ?.replace("###BP_PER###", pdpData?.revisedBuyerPremiumPercentage)
          ?.replace("###BP_AMOUNT###", getFormatPrice(pdpData?.revisedBuyerPremium));
      }
    }
    return "";
  };

  const buyersPremiumText = getBuyersPremimum();
  const showBuyersPremimum = buyersPremiumText !== "" && listingType === "AUCN";

  // EMD visibility — relaxed for commercial (shortSaleFlag may be undefined)
  const showEarnest =
    shortSaleFlag !== "Y" &&
    propCategory !== "FORECLOSURE" &&
    isPrelistAsset !== "PREVIEW";

  // Commission text logic
  const buyerAgentCommissionText =
    buyerAgentCommissionString?.length > 0 && buyerAgentCommissionString !== null
      ? buyerAgentCommissionString
      : buyerAgentCommissionPercent > 0 &&
          buyerAgentCommissionAmount !== 0 &&
          buyerAgentCommissionAmount !== null
        ? "Greater of " + buyerAgentCommissionPercent + "% of sale price or $" +
          getFormatPrice(buyerAgentCommissionAmount) + "."
        : buyerAgentCommissionAmount !== 0 && buyerAgentCommissionAmount !== null
          ? "$" + getFormatPrice(buyerAgentCommissionAmount)
          : buyerAgentCommissionPercent > 0
            ? buyerAgentCommissionPercent + "%"
            : "Not Offered";

  const showCommission = hideBuyerAgentCommissionFlag !== "Y";
  const commissionAvailable =
    buyerAgentCommissionText &&
    buyerAgentCommissionText !== "Not Offered" &&
    buyerAgentCommissionText !== "$" &&
    buyerAgentCommissionText?.length > 0;

  // Notice text
  const noticeText =
    listingType === "TRNL"
      ? "All offers must be submitted through Hubzu.com. Offers sent via email and fax will not be considered."
      : "All bids must be submitted through Hubzu.com. Bids sent via email and fax will not be considered.";

  return (
    <div className="commercial-pdp__fees-content-wrapper">
      <Tooltip id="Buyer_pays_tooltip" style={{ fontSize: 11, maxWidth: 300 }} />
      <Tooltip id="emdToolTip" style={{ fontSize: 11, maxWidth: 300 }} />

      {/* Transaction Fee Row */}
      {showBuyersPremimum && (
        <div className="commercial-pdp__fees-row">
          <div className="commercial-pdp__fees-icon commercial-pdp__fees-icon--blue">
            <img src="/assets/images/transaction_fee.png" alt="Transaction Fee" />
          </div>
          <div className="commercial-pdp__fees-content">
            <p className="commercial-pdp__fees-title">Transaction Fee</p>
            <p className="commercial-pdp__fees-description">
              {buyersPremiumText}{" "}
              <span
                data-tooltip-id="Buyer_pays_tooltip"
                data-tooltip-content="Paid by the buyer at closing, this fee covers the costs of facilitating an auction sale on Hubzu."
                style={{ cursor: "pointer" }}
              >
                (<span style={{ textDecoration: "underline dotted", cursor: "pointer" }}>
                  Buyer Pays at Closing)
                </span>
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Earnest Money Deposit Row */}
      {showEarnest && emd && emd.length > 0 && (
        <div className="commercial-pdp__fees-row">
          <div className="commercial-pdp__fees-icon commercial-pdp__fees-icon--blue">
            <img src="/assets/images/transaction_fee.png" alt="Earnest Money" />
          </div>
          <div className="commercial-pdp__fees-content">
            <p className="commercial-pdp__fees-title">Earnest Money Deposit</p>
            {emd.map((item, index) => (
              <p className="commercial-pdp__fees-description" key={item.id || item.financeType || index}>
                {item.financeType === "CASH"
                  ? `${item.financeType}- ${item.depositEarnestMoney}`
                  : `${item.financeType}: ${item.depositEarnestMoney}`}
              </p>
            ))}
            <p className="commercial-pdp__fees-subdescription">
              <span
                data-tooltip-id="emdToolTip"
                data-tooltip-content={pdpPropertyFees?.earnestMoneyTooltip}
                style={{ cursor: "pointer" }}
              >
                (<span style={{ textDecoration: "underline dotted", cursor: "pointer" }}>
                  Buyer Pays Once Purchase &amp; Sale Agreement is Fully Executed)
                </span>
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Buyer's Agent Commission Row */}
      {showCommission && (
        <div className="commercial-pdp__fees-row">
          <div className="commercial-pdp__fees-icon commercial-pdp__fees-icon--green">
            <img src="/assets/images/buyer_commision.png" alt="Buyer's Agent Commission" />
          </div>
          <div className="commercial-pdp__fees-content">
            <p className="commercial-pdp__fees-title">Buyer&apos;s Agent Commission</p>
            {commissionAvailable ? (
              <p className="commercial-pdp__fees-description--green">
                {buyerAgentCommissionText} (Seller Pays at Closing)
              </p>
            ) : brokerCoOpSellerCategoryFlag === "Y" ? (
              <p className="commercial-pdp__fees-description">Not Offered</p>
            ) : null}
          </div>
        </div>
      )}

      {/* Important Notice */}
      <div className="commercial-pdp__fees-notice">
        <strong>Important:</strong> {noticeText}
      </div>
    </div>
  );
};

export default CommercialFeesContent;
